import type React from 'react';

import { Anchor, FormField, FormHelp, Label, Select, Textarea } from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { MAXIMUM_NUMBER_OF_OCCUPANTS } from '../../../constants/index';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const MAX_OCCUPANTS = 99;

// Generate options from 1 to max
const occupantsOptions = Array.from({ length: MAX_OCCUPANTS }, (_, i) => {
  const value = i + 1;
  return { value: String(value), label: `${value} ${value === 1 ? 'person' : 'people'}` };
});

export default function MaxmimumNumberOfOccupants_v3({ section, setSectionValues }: Props): JSX.Element {
  function onNoOutstandingViolations(): void {
    setSectionValues({
      [MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS]: false,
    });
  }

  function onYesOutstandingViolations(): void {
    setSectionValues({
      [MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS]: true,
    });
  }

  function onDescriptionChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS_DESCRIPTION]: value,
    });
  }

  function onMaximumOccupancyChange(event: React.ChangeEvent<HTMLSelectElement>): void {
    setSectionValues({
      [MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.MAXIMUM_NUMBER_OF_OCCUPANTS]: Number(event.target.value),
    });
  }

  const outstandingViolationsEnabled = section.getFieldValue(
    MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS,
  );
  const outstandingViolationsDescription = section.getFieldValue(
    MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.OUTSTANDING_VIOLATIONS_DESCRIPTION,
  );
  const maximumOccupants = Number(
    section.getFieldValue(MAXIMUM_NUMBER_OF_OCCUPANTS.FIELD_NAME.MAXIMUM_NUMBER_OF_OCCUPANTS),
  );

  return (
    <>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>What is the maximum occupancy of the premises?</Label>}
        control={
          <Select value={String(maximumOccupants)} onChange={onMaximumOccupancyChange}>
            {occupantsOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        }
      />
      <FormField
        required
        label={
          <Label>Is the property subject to any outstanding violations of the City Housing Conservation Program?</Label>
        }
        description={
          <FormHelp>
            <span>
              As per local law, the City Housing Conservation District Inspection is a basic code inspection designed to
              check for minimal interior building code violations, and meet minimum exterior standards under the
              International Property Maintenance Code.{' '}
              <Anchor
                href="https://www.stlouis-mo.gov/government/departments/public-safety/building/housing-conservation.cfm"
                target="_blank"
                rel="noopener noreferrer"
                css={{ font: 'inherit' }}
              >
                Learn more
              </Anchor>
            </span>
          </FormHelp>
        }
        control={
          <YesAndNoExpansion
            yesHandler={onYesOutstandingViolations}
            noHandler={onNoOutstandingViolations}
            yesActive={outstandingViolationsEnabled}
          >
            <FormField
              required
              label={<Label>Describe these outstanding violations</Label>}
              control={
                <Textarea
                  css={{ resize: 'vertical' }}
                  value={outstandingViolationsDescription}
                  onChange={onDescriptionChange}
                />
              }
            />
          </YesAndNoExpansion>
        }
      />
    </>
  );
}
